import React from 'react';
import { styled, keyframes } from '@mui/material/styles';

// ── Shared character motion: "Jump for joy" ─────────────────────────────────
// ONE file drives every celebrating character. It defines the base hop plus
// per-part animation hooks. Each character tags only the parts it has.
//
//   .nl-char        → wrap ALL art. The little hop + squash/stretch.
//   .nl-arm-left / .nl-arm-right → arms pump, offset so they alternate.
//   .nl-mouth-laugh / .nl-mouth-grin → mouths react on the hop.
//
// Per-part hooks (character-specific):
//   .nl-hat / .nl-cap8 / .nl-cap12 / .nl-cap15 / .nl-cap19 → headwear tilt/bob
//   .nl-earring → dangly earring swing        .nl-bow → hair-bow wobble
//   .nl-band → headband jiggle                .nl-hair-l / .nl-hair-r → side hair bounce
//   .nl-blush → cheek blush pulse (fades out) .nl-pearl → necklace bead glint sweep
//   .nl-bubble → bubble-gum grow/shrink       .nl-note / .nl-noteA/B/C → music notes rise
//   .nl-shine / .nl-shine2..20 → sunglasses-lens glint (per-character colors)
//
// Pivots use transform-box:fill-box so they work regardless of viewBox size.
// The <svg> sits at zIndex:1 so <Confetti> (a zIndex:0 sibling) stays behind it.

const LOOP = '2.4s';

const kfHop = keyframes`0%,20%{transform:translateY(0) scaleX(1) scaleY(1);} 28%{transform:translateY(2px) scaleX(1.06) scaleY(.93);} 44%{transform:translateY(-2px) scaleX(.97) scaleY(1.05);} 56%{transform:translateY(0) scaleX(1.06) scaleY(.94);} 66%{transform:translateY(-1px) scaleX(.99) scaleY(1.02);} 74%{transform:translateY(0) scaleX(1.03) scaleY(.98);} 81%{transform:translateY(0) scaleX(1) scaleY(1);} 87%{transform:translateY(0) scaleX(1.01) scaleY(.99);} 93%,100%{transform:translateY(0) scaleX(1) scaleY(1);}`;
const kfArmLeft = keyframes`0%,100%{transform:rotate(-2deg);} 50%{transform:rotate(-15deg);}`;
const kfArmRight = keyframes`0%,100%{transform:rotate(2deg);} 50%{transform:rotate(15deg);}`;
const kfLaugh = keyframes`0%,20%{transform:scaleX(1) scaleY(1);} 40%{transform:scaleX(1.12) scaleY(1.35);} 60%{transform:scaleX(1.06) scaleY(1.15);} 78%{transform:scaleX(1.08) scaleY(1.22);} 90%,100%{transform:scaleX(1) scaleY(1);}`;
const kfGrin = keyframes`0%,20%{transform:scaleX(1) scaleY(1);} 38%{transform:scaleX(1.42) scaleY(1.14);} 60%{transform:scaleX(1.22) scaleY(1.06);} 78%{transform:scaleX(1.32) scaleY(1.08);} 90%,100%{transform:scaleX(1) scaleY(1);}`;
const kfHat = keyframes`0%,44%{transform:rotate(0deg);} 54%{transform:rotate(-22deg);} 66%{transform:rotate(-19deg);} 74%,100%{transform:rotate(0deg);}`;
const kfEarring = keyframes`0%,60%{transform:rotate(0deg);} 68%{transform:rotate(13deg);} 77%{transform:rotate(-9deg);} 85%{transform:rotate(5deg);} 92%{transform:rotate(-2deg);} 97%,100%{transform:rotate(0deg);}`;
const kfBow = keyframes`0%,24%{transform:rotate(0deg);} 31%{transform:rotate(12deg);} 38%{transform:rotate(-9deg);} 45%{transform:rotate(6deg);} 52%{transform:rotate(-3deg);} 60%,100%{transform:rotate(0deg);}`;
const kfShine = keyframes`0%,16%{fill:#4067AF;} 20%{fill:#9DBBEC;} 24%{fill:#FFFFFF;} 28%{fill:#9DBBEC;} 33%,100%{fill:#4067AF;}`;
const kfNoteA = keyframes`0%{opacity:0;transform:translateY(10px) rotate(-8deg) scale(.5);} 10%{opacity:1;} 34%{opacity:1;transform:translateY(-14px) rotate(6deg) scale(1);} 55%{opacity:0;transform:translateY(-40px) rotate(10deg) scale(1);} 56%,100%{opacity:0;}`;
const kfNoteB = keyframes`0%,18%{opacity:0;transform:translateY(10px) rotate(6deg) scale(.5);} 28%{opacity:1;} 52%{opacity:1;transform:translateY(-16px) rotate(-6deg) scale(1);} 72%{opacity:0;transform:translateY(-44px) rotate(-10deg) scale(1);} 73%,100%{opacity:0;}`;
const kfNoteC = keyframes`0%,36%{opacity:0;transform:translateY(10px) rotate(-6deg) scale(.5);} 46%{opacity:1;} 68%{opacity:1;transform:translateY(-14px) rotate(8deg) scale(1);} 88%{opacity:0;transform:translateY(-40px) rotate(12deg) scale(1);} 89%,100%{opacity:0;}`;
const kfGlint2 = keyframes`0%,15%{fill:#4067AF;} 22%{fill:#FFFFFF;} 30%,100%{fill:#4067AF;}`;
const kfGlint4 = keyframes`0%,15%{fill:#C9D23A;} 22%{fill:#FFFFFF;} 30%,100%{fill:#C9D23A;}`;
const kfGlint6 = keyframes`0%,15%{fill:#BF5256;} 22%{fill:#FFFFFF;} 30%,100%{fill:#BF5256;}`;
const kfGlint7 = keyframes`0%,15%{fill:#A8CCF0;} 22%{fill:#FFFFFF;} 30%,100%{fill:#A8CCF0;}`;
const kfGlint15 = keyframes`0%,15%{fill:#C9D23A;} 22%{fill:#FFFFFF;} 30%,100%{fill:#C9D23A;}`;
const kfGlint17 = keyframes`0%,15%{fill:#EDB947;} 22%{fill:#FFFFFF;} 30%,100%{fill:#EDB947;}`;
const kfGlint18 = keyframes`0%,15%{fill:#B3DB86;} 22%{fill:#FFFFFF;} 30%,100%{fill:#B3DB86;}`;
const kfGlint20 = keyframes`0%,15%{fill:#409FC1;} 22%{fill:#FFFFFF;} 30%,100%{fill:#409FC1;}`;
const kfCap8 = keyframes`0%,40%{transform:rotate(0deg);} 52%{transform:rotate(-9deg);} 64%{transform:rotate(3deg);} 74%{transform:rotate(-4deg);} 84%{transform:rotate(1deg);} 92%,100%{transform:rotate(0deg);}`;
const kfCap12 = keyframes`0%,44%{transform:rotate(0deg);} 56%{transform:rotate(7deg);} 70%{transform:rotate(-3deg);} 82%{transform:rotate(2deg);} 90%,100%{transform:rotate(0deg);}`;
const kfCap15 = keyframes`0%,46%{transform:translateY(0) rotate(0deg);} 58%{transform:translateY(-3px) rotate(-5deg);} 70%{transform:translateY(0) rotate(3deg);} 80%{transform:translateY(0) rotate(-1deg);} 90%,100%{transform:translateY(0) rotate(0deg);}`;
const kfCap19 = keyframes`0%,42%{transform:rotate(0deg);} 54%{transform:rotate(-17deg);} 66%{transform:rotate(-14deg);} 76%{transform:rotate(0deg);} 100%{transform:rotate(0deg);}`;
const kfBubble = keyframes`0%,18%{transform:scale(.7);} 55%{transform:scale(1.08);} 80%{transform:scale(.92);} 100%{transform:scale(.7);}`;
const kfBand = keyframes`0%,52%{transform:translateY(0) rotate(0deg);} 64%{transform:translateY(-1.6px) rotate(-1.6deg);} 76%{transform:translateY(0) rotate(1deg);} 86%{transform:translateY(0) rotate(-0.4deg);} 95%,100%{transform:translateY(0) rotate(0deg);}`;
const kfBlush = keyframes`0%,10%{opacity:0;transform:scale(.7);} 40%{opacity:.72;transform:scale(1.06);} 62%{opacity:.5;transform:scale(1);} 84%,100%{opacity:0;transform:scale(.78);}`;
const kfPearl = keyframes`0%,18%{fill:#BCE5F5;} 28%{fill:#FFFFFF;} 40%,100%{fill:#BCE5F5;}`;
const kfHairL = keyframes`0%,18%{transform:rotate(0deg) translateY(0);} 30%{transform:rotate(5deg) translateY(2px);} 46%{transform:rotate(-4deg) translateY(-1px);} 62%{transform:rotate(2.5deg);} 78%{transform:rotate(-1deg);} 90%,100%{transform:rotate(0deg) translateY(0);}`;
const kfHairR = keyframes`0%,18%{transform:rotate(0deg) translateY(0);} 30%{transform:rotate(-5deg) translateY(2px);} 46%{transform:rotate(4deg) translateY(-1px);} 62%{transform:rotate(-2.5deg);} 78%{transform:rotate(1deg);} 90%,100%{transform:rotate(0deg) translateY(0);}`;

export const MascotSvg = styled('svg')({
  display: 'block',
  overflow: 'visible',
  position: 'relative',
  zIndex: 1,
  '& .nl-char': {
    transformBox: 'fill-box',
    transformOrigin: 'center bottom',
    animation: `${kfHop} 2.4s cubic-bezier(.3,1.25,.5,1) infinite`,
    willChange: 'transform',
  },
  '& .nl-arm-left': {
    transformBox: 'fill-box',
    transformOrigin: '90% 95%',
    animation: `${kfArmLeft} .9s ease-in-out -0.45s infinite`,
    willChange: 'transform',
  },
  '& .nl-arm-right': {
    transformBox: 'fill-box',
    transformOrigin: '10% 95%',
    animation: `${kfArmRight} .9s ease-in-out infinite`,
    willChange: 'transform',
  },
  '& .nl-mouth-laugh': {
    transformBox: 'fill-box',
    transformOrigin: 'center center',
    animation: `${kfLaugh} 2.4s ease-in-out infinite`,
  },
  '& .nl-mouth-grin': {
    transformBox: 'fill-box',
    transformOrigin: 'center bottom',
    animation: `${kfGrin} 2.4s ease-in-out infinite`,
  },
  '& .nl-hat': {
    transformBox: 'fill-box',
    transformOrigin: 'center bottom',
    animation: `${kfHat} 2.4s ease-in-out infinite`,
    willChange: 'transform',
  },
  '& .nl-earring': {
    transformBox: 'fill-box',
    transformOrigin: 'center top',
    animation: `${kfEarring} 2.4s ease-in-out infinite`,
    willChange: 'transform',
  },
  '& .nl-bow': {
    transformBox: 'fill-box',
    transformOrigin: 'center bottom',
    animation: `${kfBow} 2.4s ease-in-out infinite`,
    willChange: 'transform',
  },
  '& .nl-shine': {
    animation: `${kfShine} 3.2s ease-in-out infinite`,
  },
  '& .nl-note': {
    transformBox: 'fill-box',
    transformOrigin: 'center',
    opacity: 0,
    willChange: 'transform,opacity',
  },
  '& .nl-noteA': {
    animation: `${kfNoteA} 3s ease-out infinite`,
  },
  '& .nl-noteB': {
    animation: `${kfNoteB} 3s ease-out infinite`,
  },
  '& .nl-noteC': {
    animation: `${kfNoteC} 3s ease-out infinite`,
  },
  '& .nl-shine2': {
    animation: `${kfGlint2}  3.4s ease-in-out infinite`,
  },
  '& .nl-shine4': {
    animation: `${kfGlint4}  3.4s ease-in-out infinite`,
  },
  '& .nl-shine6': {
    animation: `${kfGlint6}  3.4s ease-in-out infinite`,
  },
  '& .nl-shine7': {
    animation: `${kfGlint7}  3.4s ease-in-out infinite`,
  },
  '& .nl-shine15': {
    animation: `${kfGlint15} 3.4s ease-in-out infinite`,
  },
  '& .nl-shine17': {
    animation: `${kfGlint17} 3.4s ease-in-out infinite`,
  },
  '& .nl-shine18': {
    animation: `${kfGlint18} 3.4s ease-in-out infinite`,
  },
  '& .nl-shine20': {
    animation: `${kfGlint20} 3.4s ease-in-out infinite`,
  },
  '& .nl-cap8': {
    transformBox: 'fill-box',
    transformOrigin: 'center bottom',
    animation: `${kfCap8}  2.4s ease-in-out infinite`,
    willChange: 'transform',
  },
  '& .nl-cap12': {
    transformBox: 'fill-box',
    transformOrigin: 'center bottom',
    animation: `${kfCap12} 2.4s ease-in-out infinite`,
    willChange: 'transform',
  },
  '& .nl-cap15': {
    transformBox: 'fill-box',
    transformOrigin: 'center bottom',
    animation: `${kfCap15} 2.4s ease-in-out infinite`,
    willChange: 'transform',
  },
  '& .nl-cap19': {
    transformBox: 'fill-box',
    transformOrigin: 'center bottom',
    animation: `${kfCap19} 2.4s cubic-bezier(.5,.1,.4,1) infinite`,
    willChange: 'transform',
  },
  '& .nl-bubble': {
    transformBox: 'fill-box',
    transformOrigin: 'center',
    animation: `${kfBubble} 2.4s ease-in-out infinite`,
    willChange: 'transform',
  },
  '& .nl-band': {
    transformBox: 'fill-box',
    transformOrigin: 'center',
    animation: `${kfBand} 2.4s ease-in-out infinite`,
    willChange: 'transform',
  },
  '& .nl-blush': {
    transformBox: 'fill-box',
    transformOrigin: 'center',
    animation: `${kfBlush} 2.4s ease-in-out infinite`,
    willChange: 'transform,opacity',
  },
  '& .nl-pearl': {
    animation: `${kfPearl} 2.6s ease-in-out infinite`,
  },
  '& .nl-hair-l': {
    transformBox: 'fill-box',
    transformOrigin: 'center top',
    animation: `${kfHairL} 2.4s ease-in-out infinite`,
    willChange: 'transform',
  },
  '& .nl-hair-r': {
    transformBox: 'fill-box',
    transformOrigin: 'center top',
    animation: `${kfHairR} 2.4s ease-in-out infinite`,
    willChange: 'transform',
  },
  '@media (prefers-reduced-motion: reduce)': {
    '& [class*="nl-"]': { animation: 'none' },
    '& .nl-note, & .nl-blush': { opacity: 0 },
  },
});

const burst = keyframes`
  0%   { transform: translate(0, 0) scale(.2) rotate(0); opacity: 0; }
  12%  { opacity: 1; }
  24%  { transform: translate(calc(var(--tx) * .55), calc(var(--ty) * .55)) scale(1) rotate(var(--spin, 180deg)); opacity: 1; }
  100% { transform: translate(var(--tx), var(--ty)) scale(.5) rotate(var(--spin, 180deg)); opacity: 0; }
`;

// ── Confetti burst ──────────────────────────────────────────────────────────
// An absolutely-positioned overlay that radiates small pieces around the
// character. Render it as a sibling of <MascotSvg> inside a position:relative
// wrapper (the Character components already do this).

const PALETTE = ['#9C44C4', '#EC1E79', '#16A89A', '#F5A11E', '#2E5FE8', '#EE3A35', '#1FA463', '#F5A11E', '#9C44C4'];
const STAR = 'polygon(50% 0%,61% 35%,98% 35%,68% 57%,79% 91%,50% 70%,21% 91%,32% 57%,2% 35%,39% 35%)';
const rnd = (a, b) => a + Math.random() * (b - a);

function shapeStyle(i) {
  const t = i % 6;
  if (t === 0 || t === 5) return { width: `${rnd(7, 11).toFixed(0)}px`, height: '4px', borderRadius: '2px' };
  if (t === 1) { const s = rnd(4, 6).toFixed(0); return { width: `${s}px`, height: `${s}px`, borderRadius: '50%' }; }
  if (t === 2) { const s = rnd(8, 12).toFixed(0); return { width: `${s}px`, height: `${s}px`, clipPath: STAR }; }
  return { width: `${rnd(10, 14).toFixed(0)}px`, height: '3px', borderRadius: '2px' };
}

function buildPieces(n) {
  const out = [];
  for (let i = 0; i < n; i++) {
    const ang = (i / n) * Math.PI * 2 + rnd(-0.28, 0.28);
    const dist = rnd(72, 120);
    const col = PALETTE[i % PALETTE.length];
    const base = {
      '--tx': `${(Math.cos(ang) * dist).toFixed(0)}px`,
      '--ty': `${(Math.sin(ang) * dist * 0.92 - 6).toFixed(0)}px`,
      '--spin': `${rnd(-260, 260).toFixed(0)}deg`,
      '--dur': `${rnd(2.0, 2.6).toFixed(2)}s`,
      '--delay': `${(i * 0.012).toFixed(3)}s`,
      background: col,
    };
    if (i % 6 === 3) {
      out.push(
        <span key={i} style={{ ...base, background: 'transparent', width: '15px', height: '12px' }}>
          <svg viewBox="0 0 16 12" width="15" height="12" fill="none" style={{ display: 'block' }}>
            <path d="M2 6 Q4 1 6 6 Q8 11 10 6 Q12 1 14 6" stroke={col} strokeWidth="2" strokeLinecap="round" />
          </svg>
        </span>
      );
    } else {
      out.push(<span key={i} style={{ ...base, ...shapeStyle(i) }} />);
    }
  }
  return out;
}

const ConfettiBox = styled('div')({
  position: 'absolute',
  inset: 0,
  pointerEvents: 'none',
  overflow: 'visible',
  zIndex: 0,
  '& > *': {
    position: 'absolute',
    left: '50%',
    top: '34%',
    opacity: 0,
    animation: `${burst} var(--dur, 2.3s) cubic-bezier(.15,.7,.3,1) var(--delay, 0s) infinite`,
    willChange: 'transform',
  },
  '@media (prefers-reduced-motion: reduce)': { '& > *': { animation: 'none', opacity: 0 } },
});

export function Confetti({ pieces = 18, ...props }) {
  const items = React.useMemo(() => buildPieces(pieces), [pieces]);
  return <ConfettiBox {...props}>{items}</ConfettiBox>;
}
